import React, { createContext, useEffect } from "react";

export interface Step {
  name: string;
  key: string;
  description: string;
  depends?: string[];
}

interface StepWithCompletion extends Step {
  isCompleted: boolean;
}

interface StepContextType {
  step: number;
  setStep: (step: number) => void;
  nextStep: (isCompleted?: boolean) => void;
  previousStep: () => void;
  steps: StepWithCompletion[];
  markStepCompleted: (stepIndex: number) => void;
  markStepUncompleted: (stepIndex: number) => void;
}

const StepContext = createContext<StepContextType>({
  step: 0,
  setStep: () => {},
  nextStep: () => {},
  previousStep: () => {},
  steps: [],
  markStepCompleted: () => {},
  markStepUncompleted: () => {},
});

const ProvideStep = (steps: Step[], initialStep: number = 0) => {
  const [currentStep, setCurrentStep] = React.useState(initialStep);
  const [stepsWithCompletion, setStepsWithCompletion] = React.useState<
    StepWithCompletion[]
  >(steps.map((step) => ({ ...step, isCompleted: false })));

  const markStepUncompleted = (stepIndex: number) => {
    setStepsWithCompletion((prevSteps) =>
      prevSteps.map((step, index) =>
        index === stepIndex ? { ...step, isCompleted: false } : step
      )
    );
  };

  const markStepCompleted = (stepIndex: number) => {
    setStepsWithCompletion((prevSteps) =>
      prevSteps.map((step, index) =>
        index === stepIndex ? { ...step, isCompleted: true } : step
      )
    );
  };

  const nextStep = (isCompleted?: boolean) =>
    setCurrentStep((prev) => {
      const nextStepIndex = Math.min(prev + 1, steps.length - 1);

      const canGoNext =
        nextStepIndex === steps.length - 1
          ? true
          : nextStepIndex === prev + 1
          ? true
          : stepsWithCompletion[nextStepIndex].depends
          ? stepsWithCompletion[nextStepIndex].depends.every(
              (dep) =>
                stepsWithCompletion.find((s) => s.key === dep)?.isCompleted
            )
          : true;

      if (isCompleted) {
        markStepCompleted(prev);
      }

      if (!canGoNext) {
        const latestNotCompletedStep = stepsWithCompletion.findIndex(
          (step) => !step.isCompleted
        );

        return latestNotCompletedStep;
      }

      return Math.min(prev + 1, steps.length - 1);
    });
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));
  const goToStep = (step: number) =>
    setCurrentStep(Math.min(Math.max(step, 0), steps.length - 1));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  return {
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === steps.length - 1,
    stepsWithCompletion,
    markStepCompleted,
    markStepUncompleted,
  };
};

const StepProvider: React.FC<{
  children: React.ReactNode;
  steps: Step[];
  initialStep?: number;
}> = (props) => {
  const {
    currentStep,
    nextStep,
    prevStep,
    goToStep,
    stepsWithCompletion,
    markStepCompleted,
    markStepUncompleted,
  } = ProvideStep(props.steps, props.initialStep);

  const memoizedValue = React.useMemo(
    () => ({
      step: currentStep,
      setStep: goToStep,
      nextStep,
      previousStep: prevStep,
      steps: stepsWithCompletion,
      markStepCompleted,
      markStepUncompleted,
    }),
    [
      currentStep,
      nextStep,
      prevStep,
      goToStep,
      stepsWithCompletion,
      markStepCompleted,
      markStepUncompleted,
    ]
  );

  return (
    <StepContext.Provider value={memoizedValue}>
      {props.children}
    </StepContext.Provider>
  );
};

const useStep = () => {
  const context = React.useContext(StepContext);
  if (context === undefined) {
    throw new Error("useStep must be used within a StepProvider");
  }
  return context;
};

export { StepProvider, useStep };
